import React, { useState,useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    TextField,
    Typography,
    Stepper,
    Step,
    Grid,
    StepLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    Card,
    CardContent,
    Radio,
    RadioGroup,
    FormLabel
} from '@mui/material';
import './SeniorCitizen.scss'; // Import your SCSS file for styling
import membershipData from '../../data.json';
import PhoneIcon from '@mui/icons-material/Phone';

const SeniorCitizen = () => {
    // Helper function to check if the category data is valid
    const isValidCategoryData = (data, categoryType) => {
        return data && 
               typeof data === 'object' && 
               data[categoryType] && 
               Array.isArray(data[categoryType]);
    };
    
    // Helper function to safely get membership types
    const getMembershipTypes = (data, categoryType) => {
        if (!isValidCategoryData(data, categoryType)) {
            return [];
        }
        
        try {
            // Extract base membership types by removing "(New Member)" and "(Existing Member)"
            const baseTypes = data[categoryType].map(item => {
              return item.membership_type
                .replace(' (New Member)', '')
                .replace(' (Existing Member)', '');
            });
            return [...new Set(baseTypes)];
          } catch (error) {
            console.error("Error extracting membership types:", error);
            return [];
          }
        };
        
        // Helper function to safely get tenures for a membership type
        const getTenures = (data, categoryType, baseMembershipType) => {
          if (!isValidCategoryData(data, categoryType)) {
            return [];
          }
          
          try {
            // Look for both new and existing member entries for this base type
            const tenures = data[categoryType]
              .filter(item => {
                const baseType = item.membership_type
                  .replace(' (New Member)', '')
                  .replace(' (Existing Member)', '');
                return baseType === baseMembershipType;
              })
              .map(item => item.tenure);
            return [...new Set(tenures)];
          } catch (error) {
            console.error("Error extracting tenures:", error);
            return [];
          }
        };
        
        // Helper function to safely get fee details
        const getFeeDetails = (data, categoryType, baseMembershipType, tenure, isNewMember) => {
          if (!isValidCategoryData(data, categoryType)) {
            return { membershipFee: 0, entranceFee: 0, totalFee: 0, gst: 0 };
          }
          
          try {
            // Construct the full membership type based on new/existing status
            const fullMembershipType = `${baseMembershipType} (${isNewMember ? 'New Member' : 'Existing Member'})`;
            
            const item = data[categoryType].find(
              item => item.membership_type === fullMembershipType && item.tenure === tenure
            );
            
            if (item) {
              return {
                membershipFee: item.membership_fee || 0,
                entranceFee: item.entrance_fee || 0,
                gst: item.gst || 0,
                totalFee: item.grand_total || 0
              };
            }
          } catch (error) {
            console.error("Error getting fee details:", error);
          }
          
          return { membershipFee: 0, entranceFee: 0, gst: 0, totalFee: 0 };
        };

    const [formData, setFormData] = useState({
        category: '',
        name: '',
        fatherOrHusbandName: '',
        address: '',
        pinCode: '',
        dateOfBirth: '',
        age: '',
        email: '',
        gender: '',
        phoneNumber:'',
        qualification: '',
        organization: {
            name: '',
            address: '',
            phone: '',
            mobile: '',
            email: ''
        },
        preferredMailingAddress: '',
        currentDesignation: '',
        experienceYears: '',
        // Initialize with empty objects so they're not undefined
        pastExperience: [{ 
            organization: '',
            designation: '', 
            from: '', 
            to: '', 
            reasonForLeaving: '' 
        }],
        papersPublished: [{ 
            title: '', 
            occasion: '', 
            date: '', 
            remarks: '' 
        }],
        memberships: [{ 
            institution: '',
            category: '',
            professionalRemarks: '',
            socialServiceCategory: '',
            remarks: '',
        }],
        additionalInfo: '',
        introducedBy: {
            name: '',
            membershipCertificateNumber: ''
        },
        linkedInId: '',
        gstNumber: '',
        panNumber: '',
        aadhaarNumber: '',
        websiteLink: '',
        companyName: '',
        paymentdetails: '',
        MSMECertificate: '',
        natureOfIndustry: '',
        numberOfEmployees: '',
        salary: '',
        howDidYouKnowAboutUs: '',
        membershipCategory: '',
        tenure: 'Yearly',
        membershipID: ''
    });

    const [currentStep, setCurrentStep] = useState(0);
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
    const [membershipType, setMembershipType] = useState(''); // 'new' or 'old'
    const [feeDetails, setFeeDetails] = useState({
        membershipFee: 0,
        entranceFee: 0,
        gst: 0,
        totalFee: 0
    });

    // Update fee details when membership category or tenure changes (only for new members)
    useEffect(() => {
        if (formData.membershipCategory && formData.tenure && membershipType) {
            const isNewMember = membershipType === 'new';
            const newFeeDetails = getFeeDetails(
                membershipData, 
                'individual_category', 
                formData.membershipCategory,
                formData.tenure,
                isNewMember
            );
            
            setFeeDetails(newFeeDetails);
        }
    }, [formData.membershipCategory, formData.tenure, membershipType]);
    
    const handleDateOfBirthChange = (e) => {
        const { value } = e.target;

        // Allow user to input the date
        setFormData((prev) => ({
            ...prev,
            dateOfBirth: value,
        }));
    };

    const handleDateOfBirthBlur = (e) => {
        const { value } = e.target;

        // Validate input format for date of birth
        const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(value);
        if (!isValidDate) {
            alert('Please enter a valid date in the format YYYY-MM-DD.');
            setFormData((prev) => ({
                ...prev,
                dateOfBirth: '',
                age: '',
            }));
            return;
        }

        // Calculate age
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();
        if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
            age--; // Adjust for incomplete birthdays
        }

        if (age < 60) {
            alert('Only senior citizens (age 60 and above) are eligible for this category.');
        }

        setFormData((prev) => ({
            ...prev,
            dateOfBirth: value,
            age: age > 0 ? age.toString() : '0', // Ensure age is not negative
        }));
    };

    const handleMembershipTypeChange = (e) => {
        setMembershipType(e.target.value);
        // Clear member ID if switching from old to new
        if (e.target.value === 'new') {
            setFormData(prev => ({ ...prev, membershipID: '' }));
        }
    };

    // Updated handleChange to correctly handle nested array fields
    const handleChange = (e) => {
        const { name, type, value, checked } = e.target;
        
        if (type === 'checkbox') {
            setFormData((prev) => ({
                ...prev,
                [name]: checked
            }));
        } else if (name.startsWith('organization.')) {
            const key = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                organization: {
                    ...prev.organization,
                    [key]: value
                }
            }));
        } else if (name.startsWith('introducedBy.')) {
            const key = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                introducedBy: {
                    ...prev.introducedBy,
                    [key]: value
                }
            }));
        } else if (name.includes('pastExperience')) {
            // Handle pastExperience array fields with bracket notation
            const matches = name.match(/pastExperience\[(\d+)\]\.(.+)/);
            if (matches && matches.length === 3) {
                const index = parseInt(matches[1]);
                const field = matches[2];
                
                setFormData((prev) => {
                    const updatedExperiences = [...prev.pastExperience];
                    if (!updatedExperiences[index]) {
                        updatedExperiences[index] = {};
                    }
                    updatedExperiences[index][field] = value;
                    return {
                        ...prev,
                        pastExperience: updatedExperiences
                    };
                });
            }
        } else if (name.includes('papersPublished')) {
            // Handle papersPublished array fields with bracket notation
            const matches = name.match(/papersPublished\[(\d+)\]\.(.+)/);
            if (matches && matches.length === 3) {
                const index = parseInt(matches[1]);
                const field = matches[2];
                
                setFormData((prev) => {
                    const updatedPapers = [...prev.papersPublished];
                    if (!updatedPapers[index]) {
                        updatedPapers[index] = {};
                    }
                    updatedPapers[index][field] = value;
                    return {
                        ...prev,
                        papersPublished: updatedPapers
                    };
                });
            }
        } else if (name.includes('memberships')) {
            // Handle memberships array fields with bracket notation
            const matches = name.match(/memberships\[(\d+)\]\.(.+)/);
            if (matches && matches.length === 3) {
                const index = parseInt(matches[1]);
                const field = matches[2];
                
                setFormData((prev) => {
                    const updatedMemberships = [...prev.memberships];
                    if (!updatedMemberships[index]) {
                        updatedMemberships[index] = {};
                    }
                    updatedMemberships[index][field] = value;
                    return {
                        ...prev,
                        memberships: updatedMemberships
                    };
                });
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleNext = () => {
        // Validate required fields for current step before proceeding
        setCurrentStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setCurrentStep((prev) => prev - 1);
    };

    // Function to add a new experience entry
    const addExperience = () => {
        setFormData((prev) => ({
            ...prev,
            pastExperience: [
                ...prev.pastExperience,
                { organization: '', designation: '', from: '', to: '', reasonForLeaving: '' }
            ]
        }));
    };

    // Function to add a new paper entry
    const addPaper = () => {
        setFormData((prev) => ({
            ...prev,
            papersPublished: [
                ...prev.papersPublished,
                { title: '', occasion: '', date: '', remarks: '' }
            ]
        }));
    };

    // Function to add a new membership entry
    const addMembership = () => {
        setFormData((prev) => ({
            ...prev,
            memberships: [
                ...prev.memberships,
                { institution: '', category: '', professionalRemarks: '',socialServiceCategory:'', remarks: '' }
            ]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!agreeToTerms || !agreeToPrivacy) {
            alert('Please agree to the terms and privacy policy.');
            return;
        }

        if (!membershipType) {
            alert('Please select whether you are a new or existing member.');
            return;
        }

        if (membershipType === 'old' && !formData.membershipID) {
            alert('Please enter your existing membership ID.');
            return;
        }

        try {
            console.log("Submitting form data:", formData);
            // Add membership type to form data
            const formDataToSend = { ...formData, membershipType };
            const response = await axios.post('https://form.cpccbe.org/api/seniorcitizen/seniorcitizen', formDataToSend);
            alert(response.data.message || 'User registration successful!');

            // Reset form after submission
            setFormData({
                category: '',
                name: '',
                fatherOrHusbandName: '',
                address: '',
                pinCode: '',
                dateOfBirth: '',
                age: '',
                email: '',
                gender: '',
                phoneNumber:'',
                qualification: '',
                organization: {
                    name: '',
                    address: '',
                    phone: '',
                    mobile: '',
                    email: ''
                },
                preferredMailingAddress: '',
                currentDesignation: '',
                experienceYears: '',
                pastExperience: [{ 
                    organization: '', 
                    designation: '', 
                    from: '', 
                    to: '', 
                    reasonForLeaving: '' 
                }],
                papersPublished: [{ 
                    title: '', 
                    occasion: '', 
                    date: '', 
                    remarks: '' 
                }],
                memberships: [{ 
                    institution: '',
                    category: '',
                    professionalRemarks: '',
                    socialServiceCategory: '',
                    remarks: '',
                }],
                additionalInfo: '',
                introducedBy: {
                    name: '',
                    membershipCertificateNumber: ''
                },
                linkedInId: '',
                gstNumber: '',
                panNumber: '',
                aadhaarNumber: '',
                websiteLink: '',
                companyName: '',
                paymentdetails: '',
                MSMECertificate: '',
                natureOfIndustry: '',
                numberOfEmployees: '',
                salary: '',
                howDidYouKnowAboutUs: '',
                membershipCategory: '',
                tenure: 'Yearly',
                membershipID: ''
            });
            setCurrentStep(0); // Reset to the first step
            setMembershipType('');
            setAgreeToTerms(false);
            setAgreeToPrivacy(false);
            setFeeDetails({
                membershipFee: 0,
                entranceFee: 0,
                gst: 0,
                totalFee: 0
            });
        } catch (error) {
            console.error("Error submitting form:", error);
            
            // Display specific error message from server if available
            if (error.response && error.response.data && error.response.data.error) {
                alert(error.response.data.error);
            } else if (error.response && error.response.status === 409) {
                alert('This email already exists in our system. Please check your details or contact support.');
            } else {
                alert('Error submitting the form. Please try again.');
            }
        }
    };

    return (
        <Card sx={{ maxWidth: '600px', margin: 'auto', padding: '20px', marginTop: '20px' }}>
            <CardContent>
                <Typography variant="h4" component="h2" gutterBottom>
                   Senior Citizen - User Registration Form
                </Typography>

                <Stepper activeStep={currentStep} alternativeLabel>
                    {Array.from({ length: 10 }, (_, index) => (
                        <Step key={index}>
                            <StepLabel>{`Step ${index + 1}`}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <form onSubmit={handleSubmit}>

                    {/* Step 1 */}
                    {currentStep === 0 && (
                        <>
                            <Box sx={{ mt: '20px' }}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Category</InputLabel>
                                    <Select name="category" value={formData.category} onChange={handleChange} required>
                                        <MenuItem value="">Select Category</MenuItem>
                                        <MenuItem value="STUDENT">STUDENT</MenuItem>
                                        <MenuItem value="SALARIED">SALARIED</MenuItem>
                                        <MenuItem value="SELF EMPLOYED">SELF EMPLOYED</MenuItem>
                                        <MenuItem value="RETIRED">RETIRED</MenuItem>
                                        <MenuItem value="HOUSEWIFE">HOUSEWIFE</MenuItem>
                                        <MenuItem value="UNEMPLOYED">UNEMPLOYED</MenuItem>
                                        <MenuItem value="OTHERS">OTHERS</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField fullWidth margin="normal" label="Name" name="name" value={formData.name} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Father's/Husband's Name" name="fatherOrHusbandName" value={formData.fatherOrHusbandName} onChange={handleChange} required />
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    type="date"
                                    label="Date of Birth(DD-MM-YYYY)"
                                    name="dateOfBirth"
                                    value={formData.dateOfBirth}
                                    onChange={handleDateOfBirthChange}
                                    onBlur={handleDateOfBirthBlur}
                                    InputLabelProps={{ shrink: true }}
                                    required
                                />

                                <TextField fullWidth margin="normal" type="number" label="Age" name="age" value={formData.age} onChange={handleChange} required InputProps={{ readOnly: true }} />
                                <TextField fullWidth margin="normal" type="email" label="Email" name="email" value={formData.email} onChange={handleChange} required />
                                <TextField fullWidth margin="normal" label="Phone Number" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required />
                            </Box>

                            {/* Navigation Buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                            </Box>
                        </>
                     )}

                     {/* Step 2 */}
                     {currentStep === 1 && (
                         <>
                             <Box sx={{ mt: '20px' }}>
                                 <TextField fullWidth margin="normal" label="Address" name="address" value={formData.address} onChange={handleChange} required />
                                 <TextField fullWidth margin="normal" label="Pin Code" name="pinCode" value={formData.pinCode} onChange={handleChange} required />
 
                                 <FormControl fullWidth margin='normal'>
                                     <InputLabel>Gender</InputLabel>
                                     <Select name="gender" value={formData.gender} onChange={handleChange} required>
                                         <MenuItem value="">Select Gender</MenuItem>
                                         <MenuItem value="Male">Male</MenuItem>
                                         <MenuItem value="Female">Female</MenuItem>
                                     </Select>
                                 </FormControl>
 
                                 <TextField fullWidth margin='normal' label='Qualification' name='qualification' value={formData.qualification} onChange={handleChange} required />
                                 <TextField fullWidth margin='normal' label='Current Designation' name='currentDesignation' value={formData.currentDesignation} onChange={handleChange} required />
                             </Box>
 
                             {/* Navigation Buttons */}
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                 <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                 <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                             </Box>
                         </>
                     )}
 
                     {/* Step 3 */}
                     {currentStep === 2 && (
                         <>
                             <Box sx={{ mt: '20px' }}>
                                 <TextField fullWidth margin='normal' label='Organization Name' name='organization.name' value={formData.organization.name} onChange={handleChange} />
                                 <TextField fullWidth margin='normal' label='Organization Address' name='organization.address' value={formData.organization.address} onChange={handleChange}  />
                                 <TextField fullWidth margin='normal' label='Organization Phone' name='organization.phone' value={formData.organization.phone} onChange={handleChange}  />
                                 <TextField fullWidth margin='normal' label='Organization Mobile' name='organization.mobile' value={formData.organization.mobile} onChange={handleChange}  />
                                 <TextField fullWidth margin='normal' type='email' label='Organization Email' name='organization.email' value={formData.organization.email} onChange={handleChange}  />
 
                                 {/* Preferred Mailing Address */}
                                 <FormControl fullWidth margin='normal'>
                                     <InputLabel>Preferred Mailing Address</InputLabel>
                                     <Select name='preferredMailingAddress' value={formData.preferredMailingAddress} onChange={handleChange}>
                                        <MenuItem value="">Select Mailing Address</MenuItem>
                                         <MenuItem value="Home">Home</MenuItem>
                                         <MenuItem value="Office">Office</MenuItem>
                                     </Select>
                                 </FormControl>
                             </Box>
 
                             {/* Navigation Buttons */}
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                 <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                 <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                             </Box>
                         </>
                     )}
 
                     {/* Step 4 */}
                     {currentStep === 3 && (
                         <>
                             <Box sx={{ mt: '20px' }}>
                                 <TextField fullWidth margin='normal' label='Experience (Years)' type='number' name='experienceYears' value={formData.experienceYears} onChange={handleChange} required />
                                 <TextField fullWidth margin='normal' label='Company Name' name='companyName' value={formData.companyName} onChange={handleChange} required />
                                 <TextField fullWidth margin='normal' label='Nature of Industry' name='natureOfIndustry' value={formData.natureOfIndustry} onChange={handleChange} required />
 
                                 <FormControl fullWidth margin='normal'>
                                     <InputLabel>Number of Employees</InputLabel>
                                     <Select name='numberOfEmployees' value={formData.numberOfEmployees} onChange={handleChange} required>
                                         <MenuItem value="">Select Number of Employees</MenuItem>
                                         <MenuItem value="0-50">0-50</MenuItem>
                                         <MenuItem value="100-1000">100-1000</MenuItem>
                                         <MenuItem value="1000+">1000+</MenuItem>
                                     </Select>
                                 </FormControl>
                             </Box>
 
                             {/* Navigation Buttons */}
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                 <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                 <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                             </Box>
                         </>
                     )}
 
                     {/* Step 5 - Past Experience */}
                     {currentStep === 4 && (
                         <>
                             <Typography variant="h6" component="h3" gutterBottom>
                                 Past Experience
                             </Typography>
                             
                             {formData.pastExperience.map((exp, index) => (
                                 <Box key={index} sx={{ mt: '20px', p: 2, border: '1px solid #eee', borderRadius: '4px' }}>
                                     <Typography variant="subtitle1">Experience {index + 1}</Typography>
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='Organization' 
                                         name={`pastExperience[${index}].organization`} 
                                         value={exp.organization || ''} 
                                         onChange={handleChange} 
                                     />
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='Designation' 
                                         name={`pastExperience[${index}].designation`} 
                                         value={exp.designation || ''} 
                                         onChange={handleChange} 
                                     />
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='From (DD-MM-YYYY)'
                                         type='date'
                                            InputLabelProps={{ shrink: true }} 
                                         name={`pastExperience[${index}].from`} 
                                         value={exp.from || ''} 
                                         onChange={handleChange} 
                                         
                                     />
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='To (DD-MM-YYYY)'
                                            type='date'
                                            InputLabelProps={{ shrink: true }} 
                                         name={`pastExperience[${index}].to`} 
                                         value={exp.to || ''} 
                                         onChange={handleChange} 
                                   
                                     />
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='Reason for Leaving' 
                                         name={`pastExperience[${index}].reasonForLeaving`} 
                                         value={exp.reasonForLeaving || ''} 
                                         onChange={handleChange} 
                                     />
                                 </Box>
                             ))}
 
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                 <Button 
                                     variant='outlined' 
                                     onClick={addExperience}
                                 >
                                     Add Experience
                                 </Button>
 
                                 {/* Navigation Buttons */}
                                 <Box>
                                     <Button variant='contained' color='secondary' onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
                                     <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                                 </Box>
                             </Box>
                         </>
                     )}
 
                     {/* Step 6 - Papers Published */}
                     {currentStep === 5 && (
                         <>
                             <Typography variant="h6" component="h3" gutterBottom>
                                 Papers Published
                             </Typography>
                             
                             {formData.papersPublished.map((paper, index) => (
                                 <Box key={index} sx={{ mt: '20px', p: 2, border: '1px solid #eee', borderRadius: '4px' }}>
                                     <Typography variant="subtitle1">Paper {index + 1}</Typography>
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='Title' 
                                         name={`papersPublished[${index}].title`} 
                                         value={paper.title || ''} 
                                         onChange={handleChange} 
                                     />
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='Occasion' 
                                         name={`papersPublished[${index}].occasion`} 
                                         value={paper.occasion || ''} 
                                         onChange={handleChange} 
                                     />
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='Date (DD-MM-YYYY)'
                                         type='date'
                                            InputLabelProps={{ shrink: true }} 
                                         name={`papersPublished[${index}].date`} 
                                         value={paper.date || ''} 
                                         onChange={handleChange} 
                                     />
                                     <TextField 
                                         fullWidth 
                                         margin='normal' 
                                         label='Remarks' 
                                         name={`papersPublished[${index}].remarks`} 
                                         value={paper.remarks || ''} 
                                         onChange={handleChange} 
                                     />
                                 </Box>
                             ))}
 
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                 <Button 
                                     variant='outlined' 
                                     onClick={addPaper}
                                 >
                                     Add Paper
                                 </Button>
 
                                 {/* Navigation Buttons */}
                                 <Box>
                                     <Button variant='contained' color='secondary' onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
                                     <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                                 </Box>
                             </Box>
                         </>
                     )}
 
                     {/* Step 7 - Memberships */}
                     {currentStep === 6 && (
                         <>
                             <Typography variant="h6" component="h3" gutterBottom>
                                 Memberships
                             </Typography>
                             
                           {formData.memberships.map((membership, index) => (
                                                       <Box key={index} sx={{ mt: '20px', p: 2, border: '1px solid #eee', borderRadius: '4px' }}>
                                                           <Typography variant="subtitle1">Membership {index + 1}</Typography>
                                                           <TextField 
                                                               fullWidth 
                                                               margin='normal' 
                                                               label='Institution' 
                                                               name={`memberships[${index}].institution`} 
                                                               value={membership.institution || ''} 
                                                               onChange={handleChange} 
                                                           />
                                                           <TextField 
                                                               fullWidth 
                                                               margin='normal' 
                                                               label='Category' 
                                                               name={`memberships[${index}].category`} 
                                                               value={membership.category || ''} 
                                                               onChange={handleChange} 
                                                           />
                                                            <TextField 
                                                               fullWidth 
                                                               margin='normal' 
                                                               label='Professional Remarks' 
                                                               name={`memberships[${index}].professionalRemarks`} 
                                                               value={membership.professionalRemarks || ''} 
                                                               onChange={handleChange} 
                                                           />
                                                             <TextField 
                                                               fullWidth 
                                                               margin='normal' 
                                                               label='Social Service Category'
                                                               name={`memberships[${index}].socialServiceCategory`} 
                                                               value={membership.socialServiceCategory || ''} 
                                                               onChange={handleChange} 
                                                           />
                                                           <TextField 
                                                               fullWidth 
                                                               margin='normal' 
                                                               label='Remarks' 
                                                               name={`memberships[${index}].remarks`} 
                                                               value={membership.remarks || ''} 
                                                               onChange={handleChange} 
                                                           />
                                                       </Box>
                                                   ))}
 
                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                 <Button 
                                     variant='outlined' 
                                     onClick={addMembership}
                                 >
                                     Add Membership
                                 </Button>
 
                                 {/* Navigation Buttons */}
                                 <Box>
                                     <Button variant='contained' color='secondary' onClick={handleBack} sx={{ mr: 1 }}>Back</Button>
                                     <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                                 </Box>
                             </Box>
                         </>
                     )}

                    {/* Step 8 - Additional Information */}
                    {currentStep === 7 && (
                        <>
                            <Box sx={{ mt: '20px' }}>
                                <TextField 
                                    fullWidth 
                                    margin='normal' 
                                    label='Additional Information' 
                                    name='additionalInfo' 
                                    value={formData.additionalInfo} 
                                    onChange={handleChange} 
                                    multiline
                                    rows={4}
                                />
                                <TextField 
                                    fullWidth 
                                    margin='normal' 
                                    label='Introduced By Name' 
                                    name='introducedBy.name' 
                                    value={formData.introducedBy.name} 
                                    onChange={handleChange} 
                                />
                                <TextField 
                                    fullWidth 
                                    margin='normal' 
                                    label='Membership Certificate Number' 
                                    name='introducedBy.membershipCertificateNumber' 
                                    value={formData.introducedBy.membershipCertificateNumber} 
                                    onChange={handleChange} 
                                />
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                <Button variant='contained' color='primary' onClick={handleNext}>Next</Button>
                            </Box>
                        </>
                    )}

                    {/* Step 9 - Personal & Financial Details */}
                    {currentStep === 8 && (
                        <>
                            <Box sx={{ mt: '20px' }}>
                                <TextField fullWidth margin='normal' label='LinkedIn ID' name='linkedInId' value={formData.linkedInId} onChange={handleChange} />
                                <TextField fullWidth margin='normal' label='PAN Number' name='panNumber' value={formData.panNumber} onChange={handleChange} required />
                                
                                <FormControl fullWidth margin='normal'>
                                    <InputLabel>Payment Details</InputLabel>
                                    <Select name='paymentdetails' value={formData.paymentdetails} onChange={handleChange} required>
                                        <MenuItem value="">Select Payment Details</MenuItem>
                                        <MenuItem value="Online">Online</MenuItem>
                                        <MenuItem value="Cheque">Cheque</MenuItem>
                                        <MenuItem value="Cash">Cash</MenuItem>
                                    </Select>
                                </FormControl>

                                <TextField fullWidth margin='normal' label='MSME Certificate' name='MSMECertificate' value={formData.MSMECertificate} onChange={handleChange} />
                                
                                <FormControl fullWidth margin="normal" required>
                                    <InputLabel id="howDidYouKnowAboutUs-label">How did you know about us?</InputLabel>
                                    <Select 
                                        labelId="howDidYouKnowAboutUs-label"
                                        id="howDidYouKnowAboutUs"
                                        name="howDidYouKnowAboutUs" 
                                        value={formData.howDidYouKnowAboutUs} 
                                        onChange={handleChange}
                                        label="How did you know about us?"
                                    >
                                        <MenuItem value="" disabled>Select option</MenuItem>
                                        <MenuItem value="Google">Google</MenuItem>
                                        <MenuItem value="Facebook">Facebook</MenuItem>
                                        <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                                        <MenuItem value="Twitter">Twitter</MenuItem>
                                        <MenuItem value="Instagram">Instagram</MenuItem>
                                        <MenuItem value="Referral">Referral</MenuItem>
                                        <MenuItem value="Others">Others</MenuItem>  
                                    </Select>
                                </FormControl>

                                {/* Membership Type Selection */}
                                <FormControl component="fieldset" sx={{ mt: 3, mb: 2 }} required>
                                    <FormLabel component="legend">Are you a new or existing member? *</FormLabel>
                                    <RadioGroup
                                        row
                                        value={membershipType}
                                        onChange={handleMembershipTypeChange}
                                        name="membershipType"
                                    >
                                        <FormControlLabel value="new" control={<Radio />} label="New Member" />
                                        <FormControlLabel value="old" control={<Radio />} label="Existing Member" />
                                    </RadioGroup>
                                </FormControl>

                                     {/* Show Member ID field only for existing members */}
                                     {membershipType === 'old' && (
                                    <TextField 
                                        fullWidth 
                                        margin="normal" 
                                        label="Existing Membership ID" 
                                        name="membershipID" 
                                        value={formData.membershipID} 
                                        onChange={handleChange} 
                                        required 
                                        helperText="Please enter your existing membership ID"
                                        sx={{ mb: 2 }}
                                    />
                                )}

                                {/* Membership Category Selection */}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="membershipCategory-label">Membership Category</InputLabel>
                                            <Select
                                                labelId="membershipCategory-label"
                                                id="membershipCategory"
                                                name="membershipCategory"
                                                value={formData.membershipCategory}
                                                onChange={handleChange}
                                                label="Membership Category"
                                                required
                                            >
                                                <MenuItem value="" disabled>Select Membership Category</MenuItem>
                                                {getMembershipTypes(membershipData, 'individual_category').map(type => (
                                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <FormControl fullWidth margin="normal">
                                            <InputLabel id="tenure-label">Tenure</InputLabel>
                                            <Select
                                                labelId="tenure-label"
                                                id="tenure"
                                                name="tenure"
                                                value={formData.tenure}
                                                onChange={handleChange}
                                                label="Tenure"
                                                required
                                            >
                                                <MenuItem value="" disabled>Select Tenure</MenuItem>
                                                {getTenures(membershipData, 'individual_category', formData.membershipCategory).map(tenure => (
                                                    <MenuItem key={tenure} value={tenure}>{tenure}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                        
                                {/* Fee Details Display - For both new and existing members */}
                                {formData.membershipCategory && membershipType && (
                                    <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px', bgcolor: '#f5f5f5' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Fee Details - {membershipType === 'new' ? 'New Member' : 'Existing Member'}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Membership Fee (₹)"
                                                    value={feeDetails.membershipFee}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Entrance Fee (₹)"
                                                    value={membershipType === 'old' ? '0 (Waived for existing members)' : feeDetails.entranceFee}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="GST (₹)"
                                                    value={feeDetails.gst}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Total Fee (₹)"
                                                    value={feeDetails.totalFee}
                                                    InputProps={{ readOnly: true }}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ fontWeight: 'bold' }}
                                                />
                                            </Grid>
                                        </Grid>
                                        {membershipType === 'old' && (
                                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#1976d2' }}>
                                                * Entrance fee is waived for existing members
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: '20px' }}>
                                <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                <Button 
                                    variant='contained' 
                                    color='primary' 
                                    onClick={handleNext}
                                    disabled={!membershipType}
                                >
                                    Next
                                </Button>
                            </Box>
                        </>
                    )}
                    
                    {/* Step 10 - Terms and Submit */}
                    {currentStep === 9 && (
                        <>
                            <Box sx={{ mt: '20px' }}>
                                <Typography variant="h6" gutterBottom>
                                    Payment Details
                                </Typography>
                                                         
                                {/* Display selected membership details */}
                                {formData.membershipCategory && (
                                    <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px', bgcolor: '#f9f9f9' }}>
                                        <Typography variant="subtitle1" gutterBottom>
                                            Selected Membership Details:
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Member Type:</strong> {membershipType === 'new' ? 'New Member' : 'Existing Member'}
                                        </Typography>
                                        {membershipType === 'old' && (
                                            <Typography variant="body1">
                                                <strong>Existing Member ID:</strong> {formData.membershipID}
                                            </Typography>
                                        )}
                                        <Typography variant="body1">
                                            <strong>Category:</strong> {formData.membershipCategory}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Tenure:</strong> {formData.tenure}
                                        </Typography>
                                        
                                        <Typography variant="body1">
                                            <strong>Membership Fee:</strong> ₹{feeDetails.membershipFee}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>Entrance Fee:</strong> {membershipType === 'old' ? '₹0 (Waived)' : `₹${feeDetails.entranceFee}`}
                                        </Typography>
                                        <Typography variant="body1">
                                            <strong>GST:</strong> ₹{feeDetails.gst}
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1, fontSize: '1.1rem', color: '#1976d2' }}>
                                            <strong>Total Amount Payable:</strong> ₹{feeDetails.totalFee}
                                        </Typography>
                                    </Box>
                                )}
                                

                                {/* Thank you message */}
                                <Box sx={{ mt: 3, p: 3, border: '2px solid #1976d2', borderRadius: '8px', bgcolor: '#f8f9fa', textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold', mb: 2 }}>
                                        Thank you for your application for membership in THE COIMBATORE PRODUCTIVITY COUNCIL.
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        We appreciate your interest in joining our community.
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        We will review your application and get in touch shortly regarding the next steps.
                                    </Typography>
                                     <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        In the meantime, if you have any questions, please don't hesitate to contact us at +91 822 00 00 371 / 2 / 3 / 4 or cpc@cpccbe.org
                                    </Typography>
                                    <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                                        We appreciate your patience and look forward to connecting with you.
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                        TEAM – ADMIN<br />
                                        THE COIMBATORE PRODUCTIVITY COUNCIL
                                    </Typography>
                                </Box>
                                <br />
                                <hr />
                                <br />
                                
                                <FormControlLabel
                                    control={<Checkbox checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)} />}
                                    label="I agree to the terms and conditions of CPCCBE."
                                />
                                <FormControlLabel
                                    control={<Checkbox checked={agreeToPrivacy} onChange={(e) => setAgreeToPrivacy(e.target.checked)} />}
                                    label="I consent to the collection and use of my data as per the Privacy Policy."
                                />
                            </Box>
                            
                            <Box sx={{ display:'flex', justifyContent:'space-between', mt:'20px' }}>  
                                <Button variant='contained' color='secondary' onClick={handleBack}>Back</Button>
                                <Button variant='contained' color='primary' type='submit'>Submit</Button>
                            </Box>
                        </>
                    )}
                </form>
            </CardContent>
        </Card>
    );
};

export default SeniorCitizen;